import { type VariantProps, cva } from "class-variance-authority";
import Link, { type LinkProps } from "next/link";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";

import { ButtonContent } from "@/components/ui/buttons/shared/ButtonContent";
import { uiOutlineClass } from "@/components/ui/global/outline";
import { TooltipEllipsis } from "@/components/ui/overlays/Tooltip/TooltipEllipsis";

const textButton = cva(
  [
    uiOutlineClass,
    "rounded-button-rounding",
    "flex",
    "justify-center",
    "items-center",
    "gap-1",
    "p-height-xs",
    "group",
    "cursor-pointer",
    "focus-visible:outline-secondary-200",
    "disabled:text-secondary-75",
    "hover:text-neutral-400",
    "active:text-secondary-200",
  ],
  {
    variants: {
      color: {
        dual: ["text-white"],
        secondary: ["text-white"],
        primary: ["text-secondary-200"],
      },
      iconPosition: {
        left: "flex-row",
        right: "flex-row-reverse",
      },
    },
    defaultVariants: {
      color: "primary",
      iconPosition: "left",
    },
  },
);

const textButtonIcon = cva(
  [
    "h-6 w-6 flex-shrink-0",
    "group-hover:text-secondary-200",
    "group-active:text-secondary-200",
    "group-disabled:text-secondary-75",
  ],
  {
    variants: {
      color: {
        dual: ["text-secondary-200"],
        secondary: ["text-secondary-500"],
        primary: ["text-secondary-200"],
      },
    },
  },
);

type TextButtonVariantProps = VariantProps<typeof textButton>;

type TextButtonProps = TextButtonVariantProps &
  AriaButtonProps & {
    children: string;
    isLoading?: boolean;
    icon?: React.FC<React.SVGProps<SVGSVGElement>>;
    link?: LinkProps & { target?: string };
    hideText?: boolean;
    btnClassName?: string;
  };

export const TextButton = ({
  color,
  icon: Icon,
  iconPosition,
  children,
  isLoading,
  link,
  hideText,
  btnClassName,
  ...props
}: TextButtonProps) => {
  const Component: any = link ? Link : AriaButton;

  return (
    <TooltipEllipsis text={children}>
      {(onContentRef) => (
        <Component
          {...props}
          {...link}
          className={textButton({
            color,
            iconPosition,
            className: props.className,
          })}
        >
          <ButtonContent
            isLoading={isLoading}
            icon={Icon}
            text={children}
            ref={onContentRef}
            iconClassName={textButtonIcon({ color })}
            hideText={hideText}
            className={btnClassName}
          />
        </Component>
      )}
    </TooltipEllipsis>
  );
};
